/* eslint-disable max-lines */
import React, {ComponentProps, useCallback, useEffect, useMemo} from "react";
import {ChoiceUserInputAction, Competence, Challenge, Scalars, ChallengeMode, Scenario} from "../../schema";
import {classes as tourClasses} from "../../scenes/editor/challenge-edit.tour";
import Timeline from "./Timeline";
import ActionEditForm from "./actions-forms/ActionEditForm";
import {useTranslation} from "react-i18next";
import {useModal} from "../ModalProvider";
import {useAudio} from "../../providers/audio";

import ChallengeActionsAddRow from "./ChallengeActionsAddRow";
import {Action, ActionInput} from "../../types";
import {DragDropContext, Draggable, DropResult} from "react-beautiful-dnd";
import ChallengeAction from "./ChallengeAction";
import {INPUT_PHRASE_MAX_LENGTH} from "../../settings";
import ChoiceUserInputQuestionEditForm from "./actions-forms/ChoiceUserInputQuestionEditForm";

type ActionProps = ComponentProps<typeof ChallengeAction> & {
  index: number;
};

function ActionItem({index, ...props}: ActionProps) {
  return (
    <Draggable draggableId={props.action.id} index={index}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
          <ChallengeAction {...props} />
        </div>
      )}
    </Draggable>
  );
}

type props = {
  className?: string;

  challenge: Challenge;
  scenarios?: Scenario[];
  competencies?: Competence[];
  actions: Action[];

  disabled?: boolean;

  onAdd?: (actionType: Action["__typename"], data: ActionInput) => void;
  onUpdate?: (actionId: string, actionType: Action["__typename"], data: ActionInput) => void;
  onDelete?: (actionId: string) => void;
  onCopy?: (actionId: string) => void;
};

export default function ChallengeActionsList({
  className,
  actions,
  scenarios,
  competencies,
  disabled,
  ...props
}: props) {
  const {t} = useTranslation();

  const {onUpdate, onAdd} = props;

  const {add: addModal} = useModal();
  const {audioManager} = useAudio();

  const inputMaxLength = props.challenge.inputPhraseMaxLength ?? INPUT_PHRASE_MAX_LENGTH;
  const avatarMaxLength = props.challenge.avatarPhraseMaxLength ?? INPUT_PHRASE_MAX_LENGTH;
  const systemMaxLength = props.challenge.systemPhraseMaxLength ?? INPUT_PHRASE_MAX_LENGTH;

  const noAvatar = useMemo(() => {
    return props.challenge.avatar.id === "none";
  }, [props.challenge]);

  useEffect(
    () => () => {
      audioManager.getPlayback("editor")?.abortPlay();
    },
    [audioManager],
  );

  const customScores = useMemo(() => {
    return (
      props.challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
      props.challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST
    );
  }, [props.challenge.mode]);

  const customParametersSupport = useMemo(() => {
    return props.challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST;
  }, [props.challenge.mode]);

  const customParameters = useMemo(() => {
    return props.challenge.customParameters;
  }, [props.challenge.customParameters]);

  const addHandler = React.useCallback(
    (type: Action["__typename"]) => {
      onAdd && onAdd(type, {});
    },
    [onAdd],
  );

  const editActionModal = useCallback(
    (action: Action, branchId?: Scalars["ID"]) => {
      const modal = addModal(
        {
          id: `EditAction_${action.id}`,
          // size: "l",
          header: t("components.ChallengeActions.editModalTitle"),
          content: (
            <ActionEditForm
              action={action}
              branchId={branchId}
              challenge={props.challenge}
              scenarios={scenarios}
              noAvatar={noAvatar}
              customScores={customScores}
              customParametersSupport={customParametersSupport}
              customParameters={customParameters}
              inputPhraseLimit={inputMaxLength}
              avatarPhraseLimit={avatarMaxLength}
              systemPhraseLimit={systemMaxLength}
              submitDisabled={disabled}
              onSubmit={(data) => {
                onUpdate && onUpdate(action.id, action.__typename, data);
                modal.remove();
              }}
            />
          ),
        },
        true,
      );
    },
    [
      addModal,
      t,
      props.challenge,
      scenarios,
      noAvatar,
      customScores,
      customParametersSupport,
      customParameters,
      inputMaxLength,
      avatarMaxLength,
      systemMaxLength,
      disabled,
      onUpdate,
    ],
  );

  const editQuestionModal = useCallback(
    (action: ChoiceUserInputAction) => {
      const modal = addModal(
        {
          id: `EditQuestionAction_${action.id}`,
          header: t("components.ChoiceUserInputQuestionEditForm.editModalTitle"),
          content: (
            <ChoiceUserInputQuestionEditForm
              action={action}
              testId={action.id}
              submitDisabled={true}
              challenge={props.challenge}
              phraseLimit={inputMaxLength}
              onSubmit={(data) => {
                onUpdate && onUpdate(action.id, action.__typename, data);
                modal.remove();
              }}
            />
          ),
        },
        true,
      );
    },
    [addModal, t, props.challenge, inputMaxLength, onUpdate],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const {destination, source} = result;
      if (!destination) {
        return;
      }

      if (destination.index === source.index) {
        return;
      }

      const draggedAction = actions[source.index];
      const beforeActionIndex = destination.index > source.index ? destination.index + 1 : destination.index;
      if (beforeActionIndex === actions.length) {
        onUpdate &&
          onUpdate(draggedAction.id, draggedAction.__typename, {
            beforeActionId: null,
          });
      } else {
        const targetAction = actions[beforeActionIndex];
        onUpdate &&
          onUpdate(draggedAction.id, draggedAction.__typename, {
            beforeActionId: targetAction.id,
          });
      }
    },
    [actions, onUpdate],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Timeline
        className={className}
        extra={
          <ChallengeActionsAddRow
            className={tourClasses.addActionRow}
            onAdd={addHandler}
            showHelpIcon
            disabled={disabled}
          />
        }
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {actions.length > 0 ? (
              actions.map((action, index) => (
                <ActionItem
                  action={action}
                  scenarios={scenarios}
                  index={index}
                  editActionModal={editActionModal}
                  editQuestionModal={editQuestionModal}
                  disabled={disabled}
                  key={action.id}
                  {...props}
                />
              ))
            ) : (
              <ChallengeAction.Placeholder />
            )}
            {provided.placeholder}
          </div>
        )}
      </Timeline>
    </DragDropContext>
  );
}

ChallengeActionsList.Skeleton = React.memo(function () {
  return (
    <Timeline.Skeleton>
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow width={200} />
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow width={200} />
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow />
      <Timeline.SkeletonRow width={200} />
    </Timeline.Skeleton>
  );
});
